import React, { useState, useMemo } from 'react';
import { Layout, Typography, Card, Row, Col, Pagination, Tag, Tabs, Input, Select } from 'antd';
import { FaBookOpen, FaEye } from 'react-icons/fa';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Content } = Layout;
const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const blogPosts = [
  { id: 1, title: "AI Revolution in Africa", date: "2025-02-10", description: "How AI is transforming industries across Africa...", tags: ["Artificial Intelligence"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1200 },
  { id: 2, title: "Future of Cybersecurity", date: "2025-01-28", description: "Key trends and innovations in cybersecurity for 2025...", tags: ["Cybersecurity"], image: "https://www.jecthub.com/static/media/slider1.0cced2a20e757b6027db.jpg", views: 950 },
  { id: 3, title: "Big Data and You", date: "2024-12-15", description: "Understanding the impact of big data in decision-making...", tags: ["Data Science"], image: "https://www.jecthub.com/images/data-science.jpeg", views: 870 },
  { id: 4, title: "Machine Learning in Healthcare", date: "2024-11-20", description: "AI-driven innovations in healthcare and diagnostics...", tags: ["Artificial Intelligence"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1350 },
  { id: 5, title: "Cyber Threats to Watch", date: "2024-10-10", description: "The top emerging cyber threats businesses should prepare for...", tags: ["Cybersecurity"], image: "https://www.jecthub.com/images/cybersecurity.jpeg", views: 1100 },
  { id: 6, title: "AI for Social Good", date: "2025-02-18", description: "How AI is being used for social impact and solving global challenges...", tags: ["Artificial Intelligence"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1300 },
  { id: 7, title: "The Role of AI in Climate Change", date: "2025-02-05", description: "Exploring how artificial intelligence is helping fight climate change...", tags: ["Artificial Intelligence", "Environment"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1050 },
  { id: 8, title: "AI Ethics and Privacy Concerns", date: "2025-01-15", description: "An in-depth look at the ethical issues surrounding AI development...", tags: ["Artificial Intelligence", "Ethics"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1120 },
  { id: 9, title: "Cybersecurity in the Age of IoT", date: "2024-12-25", description: "How the rise of IoT is reshaping the cybersecurity landscape...", tags: ["Cybersecurity", "IoT"], image: "https://www.jecthub.com/images/cybersecurity.jpeg", views: 1020 },
  { id: 10, title: "The Future of Quantum Computing in Data Science", date: "2024-12-05", description: "How quantum computing is set to revolutionize data science and analytics...", tags: ["Data Science", "Quantum Computing"], image: "https://www.jecthub.com/images/data-science.jpeg", views: 950 },
  // ... (remaining posts unchanged for brevity, but apply same enhancements)
];

const categories = ["All", "Artificial Intelligence", "Cybersecurity", "Data Science"];
const pageSize = 4;

const dateRangeOptions = [];
const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
for (let year = 2024; year <= 2025; year++) {
  months.forEach((month, index) => {
    const monthNum = String(index + 1).padStart(2, '0');
    dateRangeOptions.push({
      label: `${year} - ${month}`,
      value: `${year}-${monthNum}`,
    });
  });
}

function NewsPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const handleCategoryChange = (key) => {
    setSelectedCategory(key);
    setCurrentPage(1);
    setSearchQuery('');
    setFromDate(null);
    setToDate(null);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleFromDateChange = (value) => {
    setFromDate(value);
    setCurrentPage(1);
  };

  const handleToDateChange = (value) => {
    setToDate(value);
    setCurrentPage(1);
  };

  const filteredPosts = useMemo(() => {
    let filtered = blogPosts;
    if (selectedCategory !== "All") {
      filtered = filtered.filter((post) => post.tags.includes(selectedCategory));
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(query) ||
          post.description.toLowerCase().includes(query)
      );
    }
    if (fromDate) {
      filtered = filtered.filter((post) => post.date >= `${fromDate}-01`);
    }
    if (toDate) {
      filtered = filtered.filter((post) => post.date <= `${toDate}-31`);
    }
    return filtered;
  }, [selectedCategory, searchQuery, fromDate, toDate]);

  const paginatedPosts = useMemo(() => {
    return filteredPosts.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [filteredPosts, currentPage]);

  return (
    <Layout>
      <Content style={{ padding: '60px', background: 'linear-gradient(135deg, #e6fffb, #fff7e6)', minHeight: '100vh' }}>
        <Title level={2} style={{ fontSize: '20px', color: '#13c2c2', textAlign: 'center', fontWeight: '700', marginBottom: '28px' }}>
          <FaBookOpen style={{ marginRight: '14px', fontSize: '20px', color: '#fa8c16' }} /> JectHub News & Insights
        </Title>
        <Paragraph style={{ fontSize: '16px', color: '#595959', textAlign: 'center', marginBottom: '32px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.6' }}>
          Stay ahead with the latest trends and insights in AI, Cybersecurity, and Data Science—curated just for you by JectHub.
        </Paragraph>

        <Row gutter={[24, 24]} style={{ marginBottom: '32px', alignItems: 'center' }}>
          <Col xs={24} md={12}>
            <Tabs
              activeKey={selectedCategory}
              onChange={handleCategoryChange}
              centered
              tabBarStyle={{ fontWeight: '700', color: '#13c2c2', borderBottom: '3px solid #13c2c2' }}
            >
              {categories.map((category) => (
                <TabPane tab={category} key={category} />
              ))}
            </Tabs>
          </Col>
          <Col xs={24} md={4}>
            <Input
              placeholder="Search posts by title or description"
              prefix={<SearchOutlined />}
              size="large"
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ borderRadius: '8px', height: '44px', boxShadow: '0 3px 6px rgba(0,0,0,0.1)', border: 'none' }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="From Date"
              size="large"
              onChange={handleFromDateChange}
              style={{ width: '100%', borderRadius: '8px' }}
              allowClear
              dropdownStyle={{ borderRadius: '8px' }}
            >
              {dateRangeOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="To Date"
              size="large"
              onChange={handleToDateChange}
              style={{ width: '100%', borderRadius: '8px' }}
              allowClear
              dropdownStyle={{ borderRadius: '8px' }}
            >
              {dateRangeOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row gutter={[24, 24]}>
          {paginatedPosts.length > 0 ? (
            paginatedPosts.map((post) => (
              <Col xs={24} sm={12} md={8} lg={6} key={post.id}>
                <Card
                  hoverable
                  cover={
                    <img
                      alt={post.title}
                      src={post.image}
                      style={{ height: '180px', objectFit: 'cover', borderRadius: '12px 12px 0 0' }}
                    />
                  }
                  style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                    background: 'linear-gradient(135deg, #ffffff, #f0fafa)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-10px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.2)';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #f0fafa, #e6fffb)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff, #f0fafa)';
                  }}
                >
                  <div style={{ padding: '16px' }}>
                    <Title level={4} style={{ marginBottom: '8px', color: '#1a1a1a', fontSize: '18px', fontWeight: '600' }}>
                      {post.title}
                    </Title>
                    <Paragraph type="secondary" style={{ fontSize: '12px', color: '#888', marginBottom: '12px' }}>
                      {post.date}
                    </Paragraph>
                    <Paragraph style={{ color: '#595959', fontSize: '14px', marginBottom: '12px' }}>
                      {post.description}{' '}
                      <Link
                        to={`/pages/News/${post.tags[0]}/${post.title.toLowerCase().replace(/\s+/g, '-')}`}
                        style={{ color: '#fa8c16', fontWeight: 'bold' }}
                      >
                        Read More
                      </Link>
                    </Paragraph>
                    <div style={{ marginBottom: '12px' }}>
                      {post.tags.map((tag) => (
                        <Tag color="geekblue" key={tag} style={{ borderRadius: '4px', marginBottom: '4px' }}>
                          {tag}
                        </Tag>
                      ))}
                    </div>
                    <Paragraph style={{ display: 'flex', alignItems: 'center', color: '#888', fontSize: '13px' }}>
                      <FaEye style={{ marginRight: '6px', color: '#13c2c2' }} /> {post.views} views
                    </Paragraph>
                  </div>
                </Card>
              </Col>
            ))
          ) : (
            <Col span={24} style={{ textAlign: 'center', marginTop: '24px' }}>
              <Paragraph style={{ color: '#999', fontSize: '16px' }}>
                No posts match your filters. Try adjusting your search or date range!
              </Paragraph>
            </Col>
          )}
        </Row>

        {filteredPosts.length > pageSize && (
          <Pagination
            current={currentPage}
            total={filteredPosts.length}
            pageSize={pageSize}
            onChange={setCurrentPage}
            style={{ marginTop: '32px', textAlign: 'center', fontSize: '16px' }}
            showSizeChanger={false}
          />
        )}

        {/* Custom CSS */}
        <style jsx global>{`
          .ant-card {
            animation: fadeIn 0.5s ease-in-out;
          }
          .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
            color: #13c2c2 !important;
          }
          .ant-tabs-ink-bar {
            background: #13c2c2 !important;
          }
          .ant-pagination-item-active {
            border-color: #fa8c16 !important;
          }
          .ant-pagination-item-active a {
            color: #fa8c16 !important;
          }
          .ant-select-selector {
            border: none !important;
            box-shadow: 0 3px 6px rgba(0,0,0,0.1) !important;
            height: 44px !important;
            display: flex !important;
            align-items: center !important;
          }
          .ant-select-selection-placeholder {
            color: #888 !important;
          }
          @keyframes fadeIn {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </Content>
    </Layout>
  );
}

export default NewsPage;